/**
 * FlexibleCRUD adapter for AI Studio Agents.
 *
 * Wraps aiStudioAgentService so FlexibleCRUDTable can list/search/paginate
 * without going through GenericCRUD (/generic/*) endpoints.
 * Catalog filters arrive via FlexibleCRUDTable.extraQueryParams.
 */

import {
  createAgent,
  deleteAgent,
  getAgent,
  listAgents,
  updateAgent,
} from '@/services/aiStudioAgentService';
import type { AiStudioAgent, StudioLifecycleStatus } from '@/types/aiStudioAgent';

type ListParams = {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: StudioLifecycleStatus;
  visibility?: string;
  scope?: string;
  domain?: string;
  tenantId?: string;
  createdBy?: string;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
};

function matchesSearch(agent: AiStudioAgent, term: string): boolean {
  const hay = `${agent.code} ${agent.name} ${agent.domain ?? ''} ${agent.createdByName ?? ''} ${agent.tenantName ?? ''}`.toLowerCase();
  return hay.includes(term);
}

function startOfDay(isoDate: string): number {
  return new Date(`${isoDate}T00:00:00`).getTime();
}

function endOfDay(isoDate: string): number {
  return new Date(`${isoDate}T23:59:59.999`).getTime();
}

function matchesCatalogFilters(agent: AiStudioAgent, query: ListParams): boolean {
  // Default catalog view: active agents only (draft, private, public) — hide archived unless explicitly filtered.
  if (query.visibility) {
    if (agent.visibility !== query.visibility) return false;
  } else if (agent.visibility === 'archived') {
    return false;
  }

  if (query.scope === 'platform' && !agent.isPlatform) return false;
  if (query.scope === 'tenant' && agent.isPlatform) return false;

  if (query.domain) {
    const domain = agent.domain?.trim() ?? '';
    if (domain !== query.domain) return false;
  }

  if (query.tenantId === '__platform__') {
    if (!agent.isPlatform || agent.tenantId) return false;
  } else if (query.tenantId) {
    if (agent.tenantId !== query.tenantId) return false;
  }

  if (query.createdBy && agent.createdBy !== query.createdBy) return false;

  if (query.createdFrom) {
    const ts = new Date(agent.createdAt).getTime();
    if (Number.isNaN(ts) || ts < startOfDay(query.createdFrom)) return false;
  }
  if (query.createdTo) {
    const ts = new Date(agent.createdAt).getTime();
    if (Number.isNaN(ts) || ts > endOfDay(query.createdTo)) return false;
  }
  if (query.updatedFrom) {
    const ts = new Date(agent.updatedAt).getTime();
    if (Number.isNaN(ts) || ts < startOfDay(query.updatedFrom)) return false;
  }
  if (query.updatedTo) {
    const ts = new Date(agent.updatedAt).getTime();
    if (Number.isNaN(ts) || ts > endOfDay(query.updatedTo)) return false;
  }

  return true;
}

function sortAgents(
  agents: AiStudioAgent[],
  sortBy?: string,
  sortOrder: 'asc' | 'desc' = 'desc'
): AiStudioAgent[] {
  if (!sortBy) return agents;
  const dir = sortOrder === 'asc' ? 1 : -1;
  return [...agents].sort((a, b) => {
    const av = a[sortBy as keyof AiStudioAgent];
    const bv = b[sortBy as keyof AiStudioAgent];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string' && typeof bv === 'string') {
      return av.localeCompare(bv) * dir;
    }
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
}

/**
 * Service object compatible with FlexibleCRUDTable (loose typing matches other adapters).
 */
export const aiStudioAgentsCRUDService = {
  async getAll(params?: ListParams | string) {
    const query: ListParams =
      typeof params === 'string' ? { search: params } : params ?? {};

    let agents = await listAgents(
      query.status
        ? { status: query.status, audience: 'factory' }
        : { audience: 'factory' }
    );

    agents = agents.filter((agent) => matchesCatalogFilters(agent, query));

    const term = query.search?.trim().toLowerCase();
    if (term) {
      agents = agents.filter((agent) => matchesSearch(agent, term));
    }

    agents = sortAgents(agents, query.sortBy ?? 'updatedAt', query.sortOrder ?? 'desc');

    const page = Math.max(1, query.page ?? 1);
    const limit = Math.max(1, query.limit ?? 20);
    const total = agents.length;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const start = (page - 1) * limit;
    const data = agents.slice(start, start + limit);

    return {
      data,
      pagination: {
        page,
        limit,
        total,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      },
    };
  },

  async getById(id: string) {
    return getAgent(id);
  },

  async create(data: Partial<AiStudioAgent>) {
    const { agent } = await createAgent({
      name: data.name?.trim() || 'Untitled agent',
      description: data.description ?? null,
      domain: data.domain ?? null,
      visibility: data.visibility ?? 'draft',
      isPlatform: data.isPlatform ?? true,
      purpose: data.purpose ?? null,
      scopeHonesty: data.scopeHonesty ?? null,
      expertPosture: data.expertPosture ?? null,
      code: data.code,
    });
    return agent;
  },

  async update(id: string, data: Partial<AiStudioAgent>) {
    return updateAgent(id, {
      name: data.name,
      code: data.code,
      description: data.description,
      domain: data.domain,
      purpose: data.purpose,
      scopeHonesty: data.scopeHonesty,
      expertPosture: data.expertPosture,
      visibility: data.visibility,
      lifecycleStatus: data.lifecycleStatus,
    });
  },

  async delete(id: string) {
    // Draft hard-delete only — backend rejects non-draft agents.
    await deleteAgent(id);
  },
};
